import { Person as PersonIcon } from "@mui/icons-material";
import Logger from "../../../services/Logger";
import {
    Box,
    Button,
    Chip,
    Grid,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import UtilService from "../../../services/UtilService";
import UserService from "../../../services/UserService";
import { usePagesTranslation } from "../../locales";
import ChangePasswordDialog from "./ChangePasswordDialog";

const ProfileInfo = () => {
    const { t } = usePagesTranslation();

    const [userProfile, setUserProfile] = useState({
        firstName: '',
        lastName: '',
        email: '',
    });
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);

    // Debounced function to update username
    const debouncedUpdateUsername = useCallback(
        debounce(async (cloudUserId, firstName, lastName) => {
            try {
                await UserService.updateUsername(cloudUserId, firstName, lastName);
                Logger.log('Username updated successfully');
            } catch (error) {
                Logger.error('Error updating username:', error);
            }
        }, 500),
        []
    );

    useEffect(() => {
        const loadUserData = async () => {
            try {
                const userInfo = await UserService.getUserInfo();
                if (!userInfo) return;

                const cloudUser = userInfo.cloudUser || {};
                const user = userInfo.user || {};
                const details = cloudUser.details || {};

                setUserProfile({
                    ...userInfo,
                    cloudUser: {
                        ...cloudUser,
                        firstName: cloudUser.firstName ?? details.firstName ?? cloudUser.first_name ?? user.given_name ?? "",
                        lastName: cloudUser.lastName ?? details.lastName ?? cloudUser.last_name ?? user.family_name ?? "",
                        email: cloudUser.email ?? user.email ?? ""
                    },
                });
            } catch (error) {
                Logger.error("Error loading profile:", error);
            }
        };
        loadUserData();
    }, []);

    const handleProfileChange = (field) => (event) => {
        const newValue = event.target.value;

        setUserProfile(prev => ({
            ...prev,
            cloudUser: {
                ...prev.cloudUser,
                [field]: newValue
            }
        }));

        // Auto-save for firstName and lastName changes
        if (field === 'firstName' || field === 'lastName') {
            const updatedProfile = {
                ...userProfile,
                cloudUser: {
                    ...userProfile.cloudUser,
                    [field]: newValue
                }
            };
            debouncedUpdateUsername(
                updatedProfile.cloudUser?.id,
                updatedProfile.cloudUser?.firstName,
                updatedProfile.cloudUser?.lastName
            );
        }
    };

    return (
        <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    {t("profile.profileInfo.title")}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, mt: 2 }}>
                    {userProfile.cloudUser && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                            <Chip
                                label={UtilService.isStandardPayPlanForUser(userProfile.cloudUser) ? t("nav.premium-member") : t("nav.free-user")}
                                color={UtilService.isStandardPayPlanForUser(userProfile.cloudUser) ? 'success' : 'default'}
                                size="small"
                            />
                            {userProfile.cloudUser?.createdAt && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {t("profile.profileInfo.member-since", {
                                        date: (() => {
                                            try {
                                                const d = typeof userProfile.cloudUser.createdAt === 'number'
                                                    ? new Date(userProfile.cloudUser.createdAt)
                                                    : new Date(userProfile.cloudUser.createdAt);
                                                return isNaN(d.getTime())
                                                    ? 'N/A'
                                                    : d.toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    });
                                            } catch (e) {
                                                return 'N/A';
                                            }
                                        })()
                                    })}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            label={t("profile.profileInfo.first-name")}
                            value={userProfile.cloudUser?.firstName || ''}
                            onChange={handleProfileChange('firstName')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            slotProps={{
                                inputLabel: {
                                    shrink: true
                                }
                            }}
                        />
                        <TextField
                            label={t("profile.profileInfo.last-name")}
                            value={userProfile.cloudUser?.lastName || ''}
                            onChange={handleProfileChange('lastName')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            slotProps={{
                                inputLabel: {
                                    shrink: true
                                }
                            }}
                        />
                    </Box>

                    <TextField
                        label={t("profile.profileInfo.email")}
                        value={userProfile?.cloudUser?.email || ''}
                        onChange={handleProfileChange('email')}
                        variant="outlined"
                        fullWidth
                        size="small"
                        disabled
                        helperText={t("profile.profileInfo.email-cannot-change")}
                        slotProps={{
                            inputLabel: {
                                shrink: true
                            }
                        }}
                    />

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setChangePasswordOpen(true)}
                        sx={{
                            alignSelf: 'flex-start',
                            borderColor: '#3BA666',
                            color: '#3BA666',
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#2e7d32',
                                color: '#2e7d32',
                                backgroundColor: 'rgba(59, 166, 102, 0.08)'
                            }
                        }}
                    >
                        {t("profile.profileInfo.changePassword.button")}
                    </Button>
                </Box>

                <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
            </Paper>
        </Grid>
    );
};

export default ProfileInfo;
